function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React, { useEffect, useRef, useCallback } from 'react';
import { noop } from 'lodash';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import filterChildren from "../../utils/Children/filterChildren";
import Icon from "../Icon/Icon";
import { isKeyPressed } from "../../../utils/isKeyPressed";
import { ESC_KEY_CODE } from "../../../utils/Browser/keyCodes";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
function getHeight(topOffset) {
  return {
    height: "calc(100% - ".concat(topOffset, ")")
  };
}
const Drawer = _ref => {
  let {
    id = "",
    className = "",
    children = [],
    open,
    secondary = false,
    hideCloseButton = false,
    style = {},
    title = null,
    topOffset = "0",
    width = "20%",
    theme = "light",
    onClose = noop,
    onEscClick = undefined
  } = _ref;
  const drawerRef = useRef(null);
  const handleEscClick = useCallback(e => {
    if (isKeyPressed(e, "Escape", ESC_KEY_CODE) && open) {
      const closeFn = onEscClick || onClose;
      closeFn();
    }
  }, [open, onEscClick, onClose]);
  useEffect(() => {
    document.addEventListener("keydown", handleEscClick, false);
    if (open && drawerRef.current) {
      drawerRef.current.focus();
    }
    return () => {
      document.removeEventListener("keydown", handleEscClick);
    };
  }, [open, handleEscClick]);
  useEffect(() => {
    if (open && drawerRef.current) {
      drawerRef.current.focus();
    }
  }, [open]);
  const handleClose = useCallback(() => {
    if (open) {
      onClose();
    }
  }, [open, onClose]);
  const drawerClass = cn('fixed top-0 h-full transition-transform duration-500 z-drawer', {
    "left-0 transform": !secondary,
    "-translate-x-full": !open && !secondary,
    "right-0 transform": secondary,
    "translate-x-full": !open && secondary,
    "translate-x-0": open,
    "bg-white": !secondary
  }, className);
  const buttonClass = cn('absolute top-2 transform h-8 w-8 rounded-full border border-gray-300 bg-white transition-transform duration-750 z-modal flex items-center justify-center mt-2 cursor-pointer', {
    "left-full ml-2 -translate-x-0": !secondary && open,
    "right-full mr-2 translate-x-0": secondary && open,
    "-translate-x-full": !open && !secondary,
    "translate-x-full": !open && secondary,
    "bg-gray-800": theme === "dark"
  });
  const newChildren = filterChildren(children);
  return /*#__PURE__*/_jsx("div", {
    ref: drawerRef,
    id: id,
    className: drawerClass,
    style: _objectSpread(_objectSpread({}, style), {}, {
      top: topOffset,
      width
    }, getHeight(topOffset)),
    tabIndex: -1,
    role: "dialog",
    "aria-modal": "true",
    "aria-labelledby": title ? "".concat(id, "-title") : undefined,
    children: /*#__PURE__*/_jsxs("div", {
      className: "relative h-full",
      children: [/*#__PURE__*/_jsxs("div", {
        className: "absolute z-drawer h-full w-full bg-white shadow-xl overflow-y-auto overflow-x-hidden",
        children: [/*#__PURE__*/_jsx("div", {
          className: "flex flex-row items-start",
          children: title && /*#__PURE__*/_jsx("div", {
            className: "flex-grow p-2",
            children: title
          })
        }), newChildren]
      }), !hideCloseButton && /*#__PURE__*/_jsx("div", {
        className: buttonClass,
        onClick: handleClose,
        children: /*#__PURE__*/_jsx(Icon, {
          ariaLabel: "Close",
          icon: "Cross",
          size: 14,
          color: theme === "dark" ? "white" : "medium-border-blue"
        })
      })]
    })
  });
};
Drawer.propTypes = {
  /** id for reference */
  id: PropTypes.string,
  /** className for reference */
  className: PropTypes.string,
  /** If true, drawer hides close button.  Defaults to false. */
  hideCloseButton: PropTypes.bool,
  /** When close button is clicked or Esc key pressed, run this function */
  onClose: PropTypes.func,
  /** When Esc key is pressed, run this function. Takes precedence over onClose function. */
  onEscClick: PropTypes.func,
  /** If true, the Drawer is open. */
  open: PropTypes.bool.isRequired,
  /** If true, the Drawer is positioned to open from the opposite side. */
  secondary: PropTypes.bool,
  /** Override the inline-styles of the root element || Not Recommended */
  style: PropTypes.object,
  /** The drawer title string or node */
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  /** How much to offset drawer from top of window */
  topOffset: PropTypes.string,
  /** The width of the Drawer in pixels or percentage in string format ex. 50% to fill half of the window or 100% and so on. */
  width: PropTypes.string,
  /**
   * children props for content inside
   * @ignore
   */
  children: PropTypes.node,
  /** theme to determine light or dark mode */
  theme: PropTypes.oneOf(["light", "dark"])
};
export default Drawer;
//# sourceMappingURL=Drawer.js.map